import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen, Target } from "lucide-react";
import coverKpis from "@/assets/cover-metricas-kpis-vendas.jpg";

const MetricasKpisVendas = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Métricas e KPIs de Vendas: Benchmarks de Mercado Para Saber se Seus Números São Bons",
    description:
      "KPI de vendas e métricas comerciais com exemplos numéricos e benchmarks por setor: taxa de conversão, ciclo, ticket médio, CAC, LTV, win rate e como medir o desempenho do vendedor.",
    image: "https://bianchimbusiness.com.br/cover-metricas-kpis-vendas.jpg",
    datePublished: "2026-08-31",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/metricas-e-kpis-de-vendas",
    },
  };

  const kpisBase = [
    {
      kpi: "Taxa de conversão (lead → cliente)",
      formula: "Clientes fechados ÷ leads trabalhados × 100",
      exemplo: "180 leads → 12 fechamentos = 6,7%",
      bench: "B2B complexo: 3% a 8% | B2B transacional: 8% a 15% | Varejo/PDV: 20% a 35%",
    },
    {
      kpi: "Win rate (proposta → contrato)",
      formula: "Propostas ganhas ÷ propostas enviadas × 100",
      exemplo: "32 propostas → 9 ganhas = 28%",
      bench: "Saudável: 20% a 30%. Abaixo de 15% = qualificação fraca. Acima de 45% = desconto demais ou pouca prospecção",
    },
    {
      kpi: "Ciclo de vendas",
      formula: "Média de dias entre 1º contato e assinatura",
      exemplo: "Soma 1.240 dias ÷ 20 negócios = 62 dias",
      bench: "Ticket até R$ 5 mil: 7 a 21 dias | R$ 5 a 50 mil: 30 a 60 dias | Acima de R$ 50 mil: 60 a 120 dias",
    },
    {
      kpi: "Ticket médio",
      formula: "Receita do período ÷ nº de contratos",
      exemplo: "R$ 264 mil ÷ 22 = R$ 12 mil",
      bench: "O que importa é a tendência: crescimento saudável fica entre 5% e 15% ao ano sem perder volume",
    },
    {
      kpi: "CAC (custo de aquisição)",
      formula: "(Marketing + comercial) ÷ novos clientes",
      exemplo: "R$ 96 mil ÷ 24 = R$ 4.000",
      bench: "Ideal: CAC pago em até 12 meses de receita do cliente. Payback acima de 18 meses aperta o caixa",
    },
    {
      kpi: "LTV / CAC",
      formula: "Receita total do cliente ÷ CAC",
      exemplo: "LTV R$ 18 mil ÷ CAC R$ 4 mil = 4,5x",
      bench: "Abaixo de 3x: modelo caro. Entre 3x e 5x: saudável. Acima de 6x: normalmente falta investimento em aquisição",
    },
    {
      kpi: "Cobertura de pipeline",
      formula: "Pipeline aberto ÷ meta do período",
      exemplo: "R$ 900 mil ÷ meta R$ 300 mil = 3x",
      bench: "Regra prática: 3x a 4x a meta. Com win rate de 25%, menos de 4x já é risco de não bater",
    },
    {
      kpi: "Follow-up / tentativas por lead",
      formula: "Total de toques ÷ leads trabalhados",
      exemplo: "1.100 toques ÷ 200 leads = 5,5",
      bench: "A maioria das vendas acontece entre o 5º e o 8º contato. Times que param no 2º toque perdem metade do potencial",
    },
  ];

  const setores = [
    { setor: "SaaS / Tecnologia B2B", conv: "4% a 8%", ciclo: "45 a 90 dias", win: "22% a 30%", obs: "Churn mensal abaixo de 2% é referência" },
    { setor: "Serviços e consultoria", conv: "10% a 20%", ciclo: "20 a 45 dias", win: "30% a 40%", obs: "Indicação responde por 30% a 50% da receita" },
    { setor: "Indústria / distribuição B2B", conv: "5% a 12%", ciclo: "60 a 120 dias", win: "25% a 35%", obs: "Recompra e share de carteira valem mais que novos logos" },
    { setor: "Imobiliário / alto valor", conv: "1% a 4%", ciclo: "60 a 180 dias", win: "15% a 25%", obs: "Volume de visitas agendadas é o KPI de topo" },
    { setor: "Seguros e benefícios", conv: "8% a 15%", ciclo: "15 a 40 dias", win: "25% a 35%", obs: "Retenção anual acima de 85% é o jogo real" },
    { setor: "Varejo e PDV", conv: "20% a 35%", ciclo: "mesmo dia a 7 dias", win: "—", obs: "Peças por atendimento e taxa de conversão de loja mandam" },
    { setor: "Educação / cursos", conv: "6% a 12%", ciclo: "7 a 30 dias", win: "25% a 35%", obs: "Show-up rate em reunião abaixo de 60% derruba tudo" },
  ];

  const vendedor = [
    { m: "Atividade (ligações + reuniões)", ruim: "< 30 toques/semana", ok: "40 a 60 toques/semana", bom: "> 70 com qualidade de conversa" },
    { m: "Reuniões realizadas por semana", ruim: "menos de 3", ok: "5 a 8", bom: "10 ou mais em ciclos curtos" },
    { m: "Win rate individual", ruim: "abaixo da média do time -10 p.p.", ok: "dentro da média ±5 p.p.", bom: "acima da média +10 p.p." },
    { m: "Atingimento de meta (quota attainment)", ruim: "abaixo de 70%", ok: "80% a 100%", bom: "acima de 110% de forma recorrente" },
    { m: "Desconto médio concedido", ruim: "acima de 15%", ok: "5% a 10%", bom: "abaixo de 5% mantendo win rate" },
    { m: "Nota em simulação de conversa", ruim: "abaixo de 60", ok: "70 a 84", bom: "85 a 100" },
  ];

  return (
    <>
      <SEOHead
        title="Métricas e KPIs de Vendas: Benchmarks por Setor (2026) | Bianchim Business"
        description="KPI vendas e métricas comerciais explicados com exemplos numéricos e benchmarks por setor. Veja como medir desempenho do vendedor e se seus números estão bons."
        keywords="KPI vendas, métricas comerciais, como medir desempenho vendedor, indicadores de vendas, benchmark de vendas, taxa de conversão, win rate, CAC, LTV, ciclo de vendas, ticket médio"
        canonicalUrl="https://bianchimbusiness.com.br/posts/metricas-e-kpis-de-vendas"
        ogType="article"
        articlePublishedTime="2026-08-31"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverKpis}
                  alt="Painel de métricas e KPIs de vendas com gráficos e indicadores"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Gestão Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Métricas e KPIs de vendas:{" "}
                    <span className="text-gradient-brand">
                      os benchmarks para saber se seus números estão bons
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>14 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>31 Ago 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Quase todo gestor comercial que eu encontro sabe quanto vendeu no mês. Poucos
                  sabem responder por que venderam aquilo. A diferença entre as duas coisas é
                  exatamente o que separa um time que bate meta por sorte de um time que bate meta
                  por processo. E é aí que entram as métricas comerciais: elas não existem para
                  enfeitar dashboard, existem para dizer onde o dinheiro está escorrendo.
                </p>
                <p>
                  Neste guia você vai encontrar os KPIs de vendas que realmente importam, com a
                  fórmula, um exemplo numérico real e o benchmark de mercado para você comparar com
                  o seu setor. No fim, uma régua prática de como medir o desempenho do vendedor sem
                  transformar a gestão em perseguição.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. Os 8 KPIs de vendas que todo time precisa acompanhar
                </h2>
                <p>
                  Se o seu relatório tem 30 indicadores, você não tem gestão — tem ruído. Comece por
                  estes oito. Eles cobrem topo, meio e fundo do funil, além da saúde financeira da
                  operação.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">KPI</th>
                        <th className="p-3 font-semibold">Como calcular</th>
                        <th className="p-3 font-semibold">Exemplo numérico</th>
                        <th className="p-3 font-semibold">Benchmark de mercado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpisBase.map((k) => (
                        <tr key={k.kpi} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{k.kpi}</td>
                          <td className="p-3">{k.formula}</td>
                          <td className="p-3">{k.exemplo}</td>
                          <td className="p-3">{k.bench}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm">
                  Os intervalos acima são faixas de referência observadas em operações B2B e B2C no
                  Brasil e servem como ponto de partida. O número que vale mesmo é o seu histórico:
                  compare-se com você de três meses atrás antes de se comparar com o mercado.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. Um exemplo completo: a conta que revela o gargalo
                </h2>
                <p>
                  Imagine uma operação com 4 vendedores e meta de R$ 300 mil/mês. No mês passado
                  entraram 400 leads, 220 foram efetivamente trabalhados, 64 viraram reunião, 32
                  receberam proposta e 9 fecharam, com ticket médio de R$ 12 mil — ou seja, R$ 108
                  mil de receita. Bateu 36% da meta.
                </p>
                <p>
                  Onde está o problema? Não é no fechamento: win rate de 28% (9 de 32) está dentro
                  do benchmark. Também não é no volume de leads. O buraco está no meio: de 220 leads
                  trabalhados só 64 viraram reunião (29%) e, pior, 45% dos leads gerados nem foram
                  tocados. Se o time trabalhasse os 400 leads com a mesma eficiência, seriam 16
                  fechamentos e R$ 192 mil. Nenhum treinamento de fechamento resolveria isso — o que
                  resolve é cadência de contato e agendamento.
                </p>
                <p>
                  Essa é a utilidade prática de um KPI: ele aponta o dedo para a etapa certa. Sem
                  essa leitura por estágio, o gestor cobra "mais garra" e o número não muda. Se você
                  ainda não tem as etapas do funil desenhadas de forma binária, vale ler antes o
                  guia de{" "}
                  <Link to="/posts/funil-vendas-2026" className="text-primary underline">
                    funil de vendas
                  </Link>
                  .
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. Benchmarks por setor: seus números estão bons?
                </h2>
                <p>
                  Comparar a taxa de conversão de uma imobiliária com a de uma loja de varejo não
                  faz sentido. Use a linha do seu setor como régua.
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Setor</th>
                        <th className="p-3 font-semibold">Conversão lead → cliente</th>
                        <th className="p-3 font-semibold">Ciclo médio</th>
                        <th className="p-3 font-semibold">Win rate</th>
                        <th className="p-3 font-semibold">O que mais pesa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {setores.map((s) => (
                        <tr key={s.setor} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{s.setor}</td>
                          <td className="p-3">{s.conv}</td>
                          <td className="p-3">{s.ciclo}</td>
                          <td className="p-3">{s.win}</td>
                          <td className="p-3">{s.obs}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/10 p-6 sm:p-8 mt-10">
                  <div className="flex items-center gap-2 mb-3 text-primary">
                    <Target className="w-5 h-5" />
                    <span className="font-semibold">Meça também a qualidade da conversa</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
                    Nota por conversa: o KPI que a planilha não te dá
                  </h3>
                  <p className="mb-5">
                    Conversão, ciclo e win rate mostram o resultado depois que a venda aconteceu (ou
                    não). O que acontece dentro da conversa — a pergunta que não foi feita, a
                    objeção mal contornada, o fechamento adiado — some do relatório. No Vendas
                    Trainer, cada vendedor treina com personas de IA do seu mercado e recebe nota de
                    0 a 100 com feedback por competência: abertura, descoberta, objeção e
                    fechamento. Você passa a ter um indicador semanal de habilidade, não só de
                    resultado.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      Criar conta grátis e medir seu time
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/calculadora-roi")}>
                      Calcular o ROI do treinamento
                    </Button>
                  </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. Como medir o desempenho do vendedor (sem virar caça às bruxas)
                </h2>
                <p>
                  Avaliar vendedor só por receita é injusto e improdutivo: o número do mês depende
                  de território, carteira, sazonalidade e sorte. A avaliação justa combina três
                  camadas — atividade (o que ele faz), eficiência (o quanto converte) e habilidade
                  (como conduz a conversa).
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Indicador individual</th>
                        <th className="p-3 font-semibold">Sinal de alerta</th>
                        <th className="p-3 font-semibold">Dentro do esperado</th>
                        <th className="p-3 font-semibold">Alta performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendedor.map((v) => (
                        <tr key={v.m} className="border-t border-border align-top">
                          <td className="p-3 font-medium text-foreground">{v.m}</td>
                          <td className="p-3">{v.ruim}</td>
                          <td className="p-3">{v.ok}</td>
                          <td className="p-3">{v.bom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p>
                  Uma leitura prática: vendedor com muita atividade e pouca conversão tem problema
                  de técnica — treine conversa. Vendedor com boa conversão e pouca atividade tem
                  problema de volume — ajuste rotina e prospecção. Vendedor ruim nos dois precisa de
                  acompanhamento próximo com prazo definido, não de mais um discurso motivacional.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. A rotina de acompanhamento que faz o número mexer
                </h2>
                <p>
                  Indicador que só é olhado no fim do mês é autópsia. O ritmo que funciona é
                  simples: diariamente o vendedor olha atividade e agenda; semanalmente o gestor
                  revisa pipeline, conversão por etapa e uma conversa gravada ou simulada de cada
                  pessoa; mensalmente a empresa revisa CAC, LTV, ticket e atingimento. Trimestre é
                  hora de rever metas e benchmarks, não de descobrir problema.
                </p>
                <p>
                  E cuidado com a métrica de vaidade: número de leads gerados, seguidores e
                  propostas enviadas sobem fácil e não pagam boleto. Se um indicador não muda uma
                  decisão sua, ele não é KPI — é curiosidade.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: número bom é número que vira ação
                </h2>
                <p>
                  Use os benchmarks deste guia para se localizar, não para se punir. Escolha dois
                  indicadores fracos, defina uma meta de melhoria de 10% em 90 dias e trate o resto
                  como monitoramento. Para aprofundar as técnicas por trás de cada etapa, veja as{" "}
                  <Link to="/posts/13-estrategias-vendas-2026" className="text-primary underline">
                    13 estratégias de vendas
                  </Link>{" "}
                  e o guia de{" "}
                  <Link to="/posts/treinamento-de-vendas" className="text-primary underline">
                    treinamento de vendas
                  </Link>
                  . Medir mostra onde dói. Treinar é o que cura.
                </p>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MetricasKpisVendas;
